import Vue from 'vue'
import api from '../api/index'
import { isEmpty } from 'lodash-es'
import { Tag } from 'ecomntag/build/ecomnTag.min'
import GoogleMapsService from '../../main/vue/googleMaps/googleMapsService'
import { getCustomerData } from '../utils/checkout'

export default {
  setOnlineStatus ({ commit }, data) {
    commit('setOnlineStatus', data)
  },
  setAllAttributes ({ state, commit }, data) {
    commit('setAllAttributes', data)
  },
  setProductBadges ({ commit }, data) {
    commit('setProductBadges', data)
  },
  setConfig ({ commit }, data) {
    commit('setConfig', data)
  },
  setSelectedProductCodeId ({ state, commit }, productCodeId) {
    const selected = state.products.find(item => {
      return item.productCodes.find(subItem => subItem.productCodeId === productCodeId)
    })
    if (selected) {
      commit('setSelectedProductCodeId', productCodeId)
    }
  },
  setSelectedAttributeId ({ state, commit }, attributeId) {
    commit('setSelectedAttributeId', attributeId)
  },
  async setLiveProducts ({ commit }, liveProducts) {
    commit('setLiveProducts', liveProducts)
  },
  async setCartProducts ({ commit }, cartProducts) {
    commit('setCartProducts', cartProducts)
  },
  async setCartLiveData ({ commit }, liveData) {
    commit('setCartLiveData', liveData)
  },
  async setGiftContents ({ commit }, giftContents) {
    commit('setGiftContents', giftContents)
  },
  setLiveProductData ({ commit }, productData) {
    commit('setLiveProduct', productData)
  },
  setCartData ({ commit }, cartData) {
    if ('liveData' in cartData) {
      commit('setCartLiveProducts', cartData.liveData)
    }

    if (cartData.hasOwnProperty('cartErrors')) {
      if (cartData.cartErrors.length === 0) {
        let successMessage = ''

        if (cartData.hasOwnProperty('addToCart')) {
          const contents = cartData.cartContents
          const quantityIncreased = contents.some(item => ['quantity_increased', 'added'].includes(item.action))
          const quantityDecreased = contents.some(item => item.action === 'quantity_decreased')
          if (quantityIncreased) {
            successMessage = Vue.locale.translate('cart.product_added')
          } else if (quantityDecreased) {
            successMessage = Vue.locale.translate('cart.product_removed')
          }
        }

        if (cartData.hasOwnProperty('removeFromCart')) {
          successMessage = Vue.locale.translate('cart.product_removed')
        }

        if (cartData.hasOwnProperty('isDigital') && cartData.isDigital) {
          if (cartData.hasOwnProperty('addToCart')) {
            successMessage = Vue.locale.translate('cart.ebook_added')
          }

          if (cartData.hasOwnProperty('removeFromCart')) {
            successMessage = Vue.locale.translate('cart.ebook_removed')
          }
        }

        if (successMessage !== '') {
          Vue.toasted.success(successMessage, {
            duration: 4000,
            icon: {
              name: 'check'
            }
          })
        }
      }
      if (cartData.cartErrors.hasOwnProperty('signatures')) {
        cartData.cartErrors.signatures.reduce((acc, item) => {
          Vue.toasted.show(item.msg, {
            duration: 20000,
            icon: {
              name: 'exclamation-triangle'
            },
            className: 'toast-warning',
            action: {
              text: Vue.locale.translate('close'),
              onClick: (e, toastObject) => {
                toastObject.goAway(0)
              }
            }
          })
          return acc
        }, [])
      }

      // inform user based on errors
      if (cartData.cartErrors.hasOwnProperty('server')) {
        cartData.cartErrors.server.reduce((acc, item) => {
          Vue.toasted.error(item.msg, {
            duration: 10000,
            icon: {
              name: 'exclamation-triangle'
            },
            action: {
              text: Vue.locale.translate('close'),
              onClick: (e, toastObject) => {
                toastObject.goAway(0)
              }
            }
          })
          return acc
        }, [])
      }

      if (cartData.cartErrors.hasOwnProperty('validation') || cartData.cartErrors.hasOwnProperty('validationOptions')) {
        commit('setCartErrors', { validation: cartData.cartErrors.validation ?? [], validationOptions:  cartData.cartErrors.validationOptions ?? [] })
      }
    }

    if (cartData.hasOwnProperty('cartContents')) {
      commit('setCartProducts', cartData.cartContents)
    }

    if (cartData.hasOwnProperty('giftContents')) {
      commit('setCartGifts', cartData.giftContents)
    }

    if (cartData.hasOwnProperty('giftRules')) {
      commit('setCartGiftRules', cartData.giftRules)
    }

    if (cartData.hasOwnProperty('defaultCoupon')) {
      commit('setDefaultCoupon', cartData.defaultCoupon)
      commit('setCouponCode', cartData.defaultCoupon.couponCode)
      commit('setCouponDiscount', cartData.defaultCoupon.couponDiscount)
    }

    if (cartData.hasOwnProperty('liveData')) {
      for (const productData of cartData.liveData) {
        commit('setLiveProduct', productData)
      }
    }

    if (
      cartData.hasOwnProperty('recommendation') &&
      cartData.recommendation.hasOwnProperty('products') &&
      cartData.recommendation.hasOwnProperty('liveData')
    ) {
      for (const productData of cartData.recommendation.liveData) {
        commit('setLiveProduct', productData)
      }
      commit('setAfterAddToCartRecommendationProducts', cartData.recommendation.products)
      commit('setAfterAddToCartRecommendationModal', !!cartData.recommendation.products)
      commit('setAfterAddToCartRecommendationRequest', !cartData.recommendation.products)
    }
  },
  addToCartClicked ({ dispatch, commit, getters }, payload) {
    const productData = getters.getSelectedProductByCodeId(payload.productCodeId)
    const afterAddToCartRecommendationRequest = getters.afterAddToCartRecommendationRequest

    const previewOrderData = getters.previewOrderData || null
    const { isInvoice, useAddress, billingCountry, shippingCountry } = getCustomerData(previewOrderData)

    if (previewOrderData) {
      Object.assign(payload, {
        invoice: isInvoice,
        useAddress: useAddress,
        billingCountry: billingCountry,
        shippingCountry: shippingCountry
      })
    }

    if (afterAddToCartRecommendationRequest) {
      payload.afterAddToCartRecommendationRequest = afterAddToCartRecommendationRequest
    }
    payload.isDigital = productData.isDigital

    return api.addToCart(payload).then(async (response) => {
      const cartData = await dispatch('reportCartEvents', { productData, item: payload, response });
      cartData.isDigital = productData.isDigital;
      dispatch('setCartData', cartData);
      return cartData;
    });
  },
  /**
   * @typedef {Object} CartItem
   * @property {string} productCodeId
   * @property {number} quantity
   * @property {any} cartProductCustomizations
   * @property {string|null} recommendationId
   * @property {string|null} recommendationType
   */

  /**
   * @typedef {Object} AddToCartPayload
   * @property {CartItem[]} items
   */

  /**
   * Action to add items to the cart.
   * @param {Object} context - The Vuex action context.
   * @param {AddToCartPayload} payload - The payload containing the items to add to the cart.
   */
  comboAddToCartClicked ({ dispatch, commit, getters }, payload) {
    return api.massAddToCart(payload.items).then(response => {
      payload.items.forEach(async (item) => {
        const productData = getters.getSelectedProductByCodeId(item.productCodeId)
        const cartData = await dispatch('reportCartEvents', { productData, item, response })
        dispatch('setCartData', cartData)
      })
    })
  },
  reportCartEvents ({ getters }, { productData, item, response }) {
    const tag = new Tag(window.dataLayer, {
      providerOptions: getters.config.tagManagerProviders,
      providerIdsPrefix: getters.config.trackingProductIdPrefix
    })
    if (item.quantity === 0) {
      response.removeFromCart = true
      if (getters.config.tagManagerEnabled) {
        tag.removeFromCart({
          name: productData.productName,
          id: productData.productId,
          quantity: item.quantity,
          price: productData.productFinalPrice
        })

        if (getters.config.tagManagerProviders.includes('googleGA4')) {
          tag.remove_from_cart_GA4({
            currency: getters.getSelectedCurrency.code,
            value: productData.productFinalPrice,
            items: [
              {
                item_id: productData.productId,
                item_name: productData.productName,
                item_brand: productData.vendorName,
                price: productData.productFinalPrice,
                discount: productData.productOldPrice - productData.productFinalPrice
              }
            ]
          })
        }
      }
    } else {
      response.addToCart = true
      if (getters.config.tagManagerEnabled) {
        tag.addToCart({
          name: productData.productName,
          id: productData.productId,
          productCodeId: item.productCodeId,
          quantity: item.quantity,
          price: productData.productFinalPrice
        })

        if (getters.config.tagManagerProviders.includes('googleGA4')) {
          tag.add_to_cart_GA4({
            currency: getters.getSelectedCurrency.code,
            value: productData.productFinalPrice,
            items: [
              {
                item_id: productData.productId,
                item_name: productData.productName,
                item_brand: productData.vendorName,
                price: productData.productFinalPrice,
                discount: productData.productOldPrice - productData.productFinalPrice
              }
            ]
          })
        }
      }
    }
    return response
  },
  openProductCardModal ({ commit }, productId) {
    commit('openProductCardModal', productId)
  },
  closeProductCardModal ({ commit }) {
    commit('closeProductCardModal')
  },
  addGiftToSelection ({ commit }, { giftId, productId }) {
    commit('setGiftToSelectedGifts', { giftId: giftId, productId: productId, quantity: 1 })
  },
  removeGiftFromSelection ({ commit }, { giftId, productId }) {
    commit('removeGiftFromSelectedGifts', { giftId: giftId, productId: productId })
  },
  getTransporterData ({ commit, dispatch, getters }, payload) {
    commit('setTransportersLoading', true)
    api.getTransporterData(payload).then(response => {
      commit('setTransporters', response)
      commit('setTransporterId', null)
    }).finally(() => {
      commit('setTransportersLoading', false)
      const courierTransporter = getters.getTransporters.find(transporter => transporter.class_name === 'NO-PROVIDER')
      if (!getters.getSelectedTransporterClassName && courierTransporter && getters.previewOrderData.customerData.country === 'GR' && getters.previewOrderIsCourierAddress === '1') {
        commit('setSelectedTransporterId', courierTransporter.transporter_id)
        dispatch('setPreviewOrderTransporterId', courierTransporter.transporter_id)
      }
    })
  },
  setPreviewOrderData ({ commit }, payload) {
    commit('setPreviewOrderData', payload)
  },
  setPreviewOrderCustomerEmail ({ commit }, payload) {
    commit('setPreviewOrderCustomerEmail', payload)
  },
  async checkCoupon (
    { commit },
    { couponCode, invoice, useAddress, billingCountry, shippingCountry, abortController = null }
  ) {
    commit('setCouponDiscount', null)
    commit('setCouponCode', couponCode)
    const response = await api.checkCoupon({
      couponCode,
      invoice,
      useAddress,
      billingCountry,
      shippingCountry,
      abortController
    })
    commit('setCouponDiscount', response.discount)
    commit('setCouponMessage', response.message)
  },
  setPreviewOrderTransporterId ({ commit }, transporterId) {
    commit('setTransporterId', transporterId)
  },
  popupMinicartGiftModal ({ commit }, payload) {
    commit('setMinicartGiftChoiceModal', payload)
  },
  setAllTransporterSmartPoints ({ commit, state, getters }, data) {
    commit('setAllTransporterSmartPoints', data)
    return false
  },
  smartPointUncheck ({ commit }) {
    const smartPointCheckbox = document.getElementById('smartPointChecked')
    if (smartPointCheckbox) {
      smartPointCheckbox.checked = false
    }
    commit('setSmartPointIsSelected', false)
    commit('setSelectedSmartPoint', null)
  },
  setUriWithoutOffset ({ commit }, payload) {
    commit('setUriWithoutOffset', payload)
  },
  setIsTopLevelCategory ({ commit }, payload) {
    commit('setIsTopLevelCategory', payload)
  },
  setCurrentPageNumber ({ commit }, payload) {
    commit('setCurrentPageNumber', payload)
  },
  setProductCategoryData ({ commit }, payload) {
    commit('setProductCategoryData', payload)
  },
  setSearchInputSmartPoints ({ commit }, payload) {
    return commit('setSearchInputSmartPoints', payload)
  },
  setMaxResultsSmartPoints ({ commit }, payload) {
    commit('setMaxResultsSmartPoints', payload)
  },
  setClosestSmartPoints ({ commit }, payload) {
    return commit('setClosestSmartPoints', payload)
  },
  setCachedFilteredSearchedClosestSmartPoints ({ commit }, payload) {
    commit('setCachedFilteredSearchedClosestSmartPoints', payload)
  },
  setSelectedSmartPoint ({ commit }, payload) {
    return commit('setSelectedSmartPoint', payload)
  },
  smartPointMapCloseEvent ({ commit }, payload) {
    return commit('smartPointMapCloseEvent', payload)
  },
  smartPointMapOpenEvent ({ commit }, payload) {
    return commit('smartPointMapOpenEvent', payload)
  },
  setGoogleMapLoading ({ commit }, payload) {
    return commit('setGoogleMapLoading', payload)
  },
  setMapPoints ({ commit }, payload) {
    commit('setMapPoints', payload)
  },
  async initializeGoogleService ({ commit, getters }, useClusters) {
    const selectedTransporterId = getters.getSelectedTransporterId
    const service = await new GoogleMapsService(getters.googleMapsApiKey, useClusters, selectedTransporterId)
    await commit('setGoogleService', service)

    await getters.googleService.createMap([])
    await getters.googleService.setCurrentUserLocation()

    return service
  },
  setCurrentUserGeolocation ({ commit }, payload) {
    return commit('setCurrentUserGeolocation', payload)
  },
  setCurrentUserGeolocationError ({ commit }, payload) {
    return commit('setCurrentUserGeolocationError', payload)
  },
  async smartPointClickEvent ({ dispatch, commit, getters }) {
    if (isEmpty(getters.googleService)) {
      await dispatch('initializeGoogleService', true)
    } else {
      await dispatch('initMapPointsGoogleService')
    }
    await commit('setSmartPointIsSelected', true)

    if (getters.getCustomerPostalCode !== '' && getters.getCustomerPostalCode !== getters.getPreviousPostalCodeInput) {
      await dispatch('setSearchInputSmartPoints', getters.getCustomerPostalCode)
    }

    if (
      getters.getSearchInputSmartPoints === '' &&
      getters.getCachedFilteredSearchedClosestSmartPoints.length > 0 &&
      Number(getters.googleService.transporterId) === Number(getters.getSelectedTransporterId)
    ) { return }

    await dispatch('initMapPointsGoogleService')
    getters.googleService.transporterId = Number(getters.getSelectedTransporterId)
  },
  async initMapPointsGoogleService ({ getters }) {
    await getters.googleService.initMapPoints(getters.getSelectedTransporterSmartPoints)
    if (getters.getSearchInputSmartPoints !== '' && getters.getSearchInputSmartPoints !== getters.getPreviousPostalCodeInput) {
      await getters.googleService.findClosest(getters.getSearchInputSmartPoints, getters.getSelectedTransporterSmartPoints, getters.getMaxResultsSmartPoints)
    } else {
      await getters.googleService.findClosest(getters.getCurrentUserGeolocation, getters.getSelectedTransporterSmartPoints, getters.getMaxResultsSmartPoints)
    }
  },
  setCheckoutLocalErrors ({ commit }, payload) {
    return commit('setCheckoutLocalErrors', payload)
  },
  validateVat ({ state, commit }, data) {
    clearTimeout(state.checkoutVatApiDebounceTimeout)
    const timeout = setTimeout(() => {
      commit('setVatError', false)
      commit('setEmptyVatData')
      if (data.vat) {
        api.validateVat(data).then(response => {
          commit('setVatError', !response.result.valid)
          if (response.result.valid) {
            commit('setVatData', response.result.data)
          }
        })
      }
    }, 800)
    commit('setCheckoutVatApiDebounceTimeout', timeout)
  },
  setSelectedCurrency ({ commit }, selectedCurrency) {
    commit('setSelectedCurrency', selectedCurrency)
  },
  setAfterAddToCartRecommendationRequest ({ commit }, payload) {
    commit('setAfterAddToCartRecommendationRequest', payload)
  },
  setAfterAddToCartRecommendationModal ({ commit }, payload) {
    commit('setAfterAddToCartRecommendationModal', payload)
  },
  setActiveTagFilters ({ commit }, payload) {
    commit('setActiveTagFilters', payload)
  },
  setActiveAuthorFilters ({ commit }, payload) {
    commit('setActiveAuthorFilters', payload)
  },
  setAuthorFilterQueryStringVar ({ commit }, payload) {
    commit('setAuthorFilterQueryStringVar', payload)
  },
  setTagRecordsUsed ({ commit }, payload) {
    commit('setTagRecordsUsed', payload)
  },
  setCurrentFilters ({ commit }, payload) {
    commit('setCurrentFilters', payload)
  },
  setFilterQueryStringVar ({ commit }, payload) {
    commit('setFilterQueryStringVar', payload)
  },
  setListingTagGroupCombos ({ commit }, payload) {
    commit('setListingTagGroupCombos', payload)
  },
  setExternalTransporterRates ({ commit }, payload) {
    commit('setExternalTransporterRates', payload)
  },
  setExternalTransporterRatesLoading ({ commit }, payload) {
    commit('setExternalTransporterRatesLoading', payload)
  },
  setSelectedTransporterExternalRate ({ commit }, payload) {
    commit('setSelectedTransporterExternalRate', payload)
  },
  async getExternalTransporterCost ({ getters, commit }) {
    const selectedTransporter = getters.getSelectedTransporterObject

    if (!selectedTransporter) {
      return
    }

    const isExternalTransporterCost = getters.getIsExternalTransporterCost(selectedTransporter.transporter_id)

    if (!isExternalTransporterCost) {
      return
    }

    const rates = selectedTransporter.externalServices

    if (rates.length) {
      commit('setExternalTransporterRates', rates)
    }
    commit('setExternalTransporterRatesLoading', false)
  },
  setCustomerLoggedInStatus ({ commit}, payload) {
    commit('setCustomerLoggedInStatus', payload)
  },
  setGiftPackaging ({ commit }, payload) {
    commit('setGiftPackaging', payload)
  },
  setGiftPackagingMessage ({ commit }, payload) {
    commit('setGiftPackagingMessage', payload)
  },
  openPaymentSessionAction ({ commit }, payload) {
    api.doOpenPaymentSession(payload).then(response => {
      commit('klarnaPaymentSessionMutation', response)
    })
  }
}
